import React, { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { Icon } from '@iq/react-components';
import ListItem from '../../../../ListItem/ListItem';
import TypeLabel from '../../../../TypeLabel';
import ConfirmationDialog from '../../../../ConfirmationDialog/ConfirmationDialog';
import LabelWithTooltip from '../../../../LabelWithTooltip/LabelWithTooltip';
import EditModal from '../EditModal';
import { getActiveSite } from '../../../../../bundles/sites';
import {
  updateNotificationConfig,
  createNotificationConfig,
  deleteNotificationConfig,
  setCopiedSettings,
  getCopiedSettings,
} from '../../../../../bundles/notification-config';
import {
  ExpansionRow,
  useExpandedSet,
  buildCopyAction,
  buildPasteAction,
  buildResetAction,
} from '../shared';
import { isConfigCustom } from '../../constants';

const COLUMN_WIDTHS = { type: 'rem', widths: [4, 18, 14, 6, 18, 'fill'] };

const SPACERS = {
  caret: '4rem',
  user: '18rem',
  role: '14rem',
  onoff: '6rem',
  actions: '7rem',
};

const EXPANSION_SPACERS = {
  before: [
    [SPACERS.caret, 'user-expansion__col-caret'],
    [SPACERS.user, 'user-expansion__col-user'],
    [SPACERS.role, 'user-expansion__col-role'],
  ],
  onoffWidth: undefined,
  deliveryWidth: undefined,
  after: [[SPACERS.actions, 'user-expansion__col-options']],
};

const UserList = ({ users }) => {
  const dispatch = useDispatch();
  const site = useSelector(getActiveSite);
  const copiedSettings = useSelector(getCopiedSettings);
  const {
    expandedIds: expandedUsers,
    allExpanded,
    toggleAll,
    toggleOne: toggleUser,
  } = useExpandedSet(users);
  const [editingUser, setEditingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [pasteTarget, setPasteTarget] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [matchesRoleTarget, setMatchesRoleTarget] = useState(null);

  const handleSave = (user, data) => {
    // If settings now match the role defaults, prompt to remove the custom config instead
    if (user.configId && !isConfigCustom(user.roleName, data)) {
      setMatchesRoleTarget(user);
      setEditingUser(null);
      return;
    }

    if (user.configId) {
      dispatch(updateNotificationConfig(user.configId, data));
    } else {
      dispatch(
        createNotificationConfig({
          eventType: 'dtmp',
          user: user.user,
          org: site.org,
          site: site.id,
          ...data,
        })
      );
    }
    setEditingUser(null);
  };

  const handleMatchesRoleConfirm = () => {
    if (matchesRoleTarget?.configId) {
      dispatch(deleteNotificationConfig(matchesRoleTarget.configId));
    }
    setMatchesRoleTarget(null);
  };

  const handleDeleteConfirm = () => {
    if (deletingUser?.configId) {
      dispatch(deleteNotificationConfig(deletingUser.configId));
    }
    setDeletingUser(null);
  };

  const handleCopy = (user) => {
    dispatch(setCopiedSettings(user.name || user.user, user.stateChangeSettings));
  };

  const handlePasteConfirm = () => {
    if (pasteTarget && copiedSettings) {
      const data = { stateChangeSettings: { ...copiedSettings.stateChangeSettings } };
      // If pasted settings match the role, prompt to remove the custom config
      if (pasteTarget.configId && !isConfigCustom(pasteTarget.roleName, data)) {
        setMatchesRoleTarget(pasteTarget);
        setPasteTarget(null);
        return;
      }
      handleSave(pasteTarget, data);
    }
    setPasteTarget(null);
  };

  const handleResetConfirm = () => {
    if (resetTarget?.configId) {
      dispatch(deleteNotificationConfig(resetTarget.configId));
    }
    setResetTarget(null);
  };

  const getMenuActions = (user) => {
    const actions = [
      { text: 'Edit', action: () => setEditingUser(user), placement: 'before' },
      buildCopyAction(() => handleCopy(user), 'Copy notification settings from this user.'),
      buildPasteAction({
        copiedSettings,
        currentSettings: user.stateChangeSettings,
        onPaste: () => setPasteTarget(user),
      }),
    ];

    if (user.hasCustomConfig) {
      actions.push(
        buildResetAction(
          () => setResetTarget(user),
          "Reset this user's notification settings to match their role. User customizations will be lost."
        )
      );
    }

    return actions;
  };

  const headerColumns = useMemo(
    () => [
      <div
        key="caret"
        className="user-caret-cell"
        onClick={toggleAll}
      >
        <Icon
          icon={allExpanded ? 'he-up' : 'he-down'}
          size="xs"
        />
      </div>,
      <div key="user">User</div>,
      <div key="role">Role</div>,
      <div key="onoff">On/Off</div>,
      <div key="conditions">Conditions</div>,
      <div key="delivery">Delivery Options</div>,
    ],
    [allExpanded, toggleAll]
  );

  if (users.length === 0) {
    return (
      <div className="user-list__empty">
        <Icon
          icon="he-info"
          size="s"
        />
        <span>No custom notification rules have been created for any users.</span>
      </div>
    );
  }

  return (
    <div className="user-list">
      <ListItem
        isHeader
        columns={headerColumns}
        columnWidths={COLUMN_WIDTHS}
        withActionHeader
      />

      {users.map((user, index) => {
        const expanded = expandedUsers.has(user.id);
        const zebraClass = index % 2 === 0 ? 'user-row--odd' : 'user-row--even';

        const columns = [
          <div
            key="caret"
            className="user-caret-cell"
          >
            <Icon
              icon={expanded ? 'he-up' : 'he-down'}
              size="xs"
            />
          </div>,
          <div
            key="user"
            className="user-name-content"
          >
            <LabelWithTooltip name={user.user} />
          </div>,
          <div
            key="role"
            className="user-role-content"
          >
            <LabelWithTooltip name={user.roleName} />
            {user.hasCustomConfig && (
              <TypeLabel
                text="Custom"
                color="grey"
              />
            )}
          </div>,
          <div key="onoff" />,
          <div
            key="conditions"
            className="user-row__conditions-label"
          >
            {expanded && 'Notify when alarm reaches status:'}
          </div>,
          <div key="delivery" />,
        ];

        return (
          <React.Fragment key={user.id}>
            <div
              className={`user-row-wrapper ${zebraClass}`}
              onClick={(event) => {
                if (event.target.closest('.list-item-menu, .dropdown-menu')) return;
                toggleUser(user.id);
              }}
            >
              <ListItem
                item={user}
                itemIndex={index}
                columns={columns}
                columnWidths={COLUMN_WIDTHS}
                disableDefaultActions
                extraMenuActions={getMenuActions(user)}
              />
            </div>
            <div
              className={`user-expansion-wrapper ${zebraClass}${expanded ? ' user-expansion-wrapper--open' : ''}`}
            >
              <div className={`user-expansion ${zebraClass}`}>
                <ExpansionRow
                  settings={user.stateChangeSettings || {}}
                  prefix="user-expansion"
                  spacers={EXPANSION_SPACERS}
                />
              </div>
            </div>
          </React.Fragment>
        );
      })}

      {editingUser && (
        <EditModal
          config={{ stateChangeSettings: editingUser.stateChangeSettings }}
          roleName={editingUser.name || editingUser.user}
          onClose={() => setEditingUser(null)}
          onSave={(data) => handleSave(editingUser, data)}
        />
      )}

      {pasteTarget && copiedSettings && (
        <ConfirmationDialog
          title="Paste notification settings"
          body={
            <>
              <p>
                You are pasting settings from <strong>{copiedSettings.sourceRole}</strong> to{' '}
                <strong>{pasteTarget.name || pasteTarget.user}</strong>. This will overwrite the
                current settings.
              </p>
              <p style={{ marginTop: '1rem' }}>
                Are you sure you want to replace the current settings?
              </p>
            </>
          }
          confirmText="Paste"
          confirmType="primary"
          showCancel
          onConfirm={handlePasteConfirm}
          onCancel={() => setPasteTarget(null)}
          className="size-s"
        />
      )}

      {resetTarget && (
        <ConfirmationDialog
          title="Reset to match role"
          body={
            <>
              <p>
                This will reset notification settings for{' '}
                <strong>{resetTarget.name || resetTarget.user}</strong> to match their role (
                <strong>{resetTarget.roleName}</strong>). All user customizations will be lost{' '}
                <strong>
                  and {resetTarget.name || resetTarget.user} will be removed from this table.
                </strong>
              </p>
              <p style={{ marginTop: '1rem' }}>Are you sure you want to reset?</p>
            </>
          }
          confirmText="Reset"
          confirmType="danger"
          showCancel
          onConfirm={handleResetConfirm}
          onCancel={() => setResetTarget(null)}
          className="size-s"
        />
      )}

      {deletingUser && (
        <ConfirmationDialog
          title="Delete user customization"
          body={
            <>
              <p>
                Are you sure you want to delete the notification customization for{' '}
                <strong>{deletingUser.name || deletingUser.user}</strong>? This user will revert to
                the role-level default settings.
              </p>
            </>
          }
          confirmText="Delete"
          confirmType="danger"
          showCancel
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeletingUser(null)}
          className="size-s"
        />
      )}

      {matchesRoleTarget && (
        <ConfirmationDialog
          title="Settings match role defaults"
          body={
            <>
              <p>
                The notification settings for{' '}
                <strong>{matchesRoleTarget.name || matchesRoleTarget.user}</strong> now match the
                defaults for the <strong>{matchesRoleTarget.roleName}</strong> role.
              </p>
              <p style={{ marginTop: '1rem' }}>
                Would you like to remove the custom config and use the role defaults instead?
              </p>
            </>
          }
          confirmText="Remove custom config"
          confirmType="primary"
          showCancel
          onConfirm={handleMatchesRoleConfirm}
          onCancel={() => setMatchesRoleTarget(null)}
          className="size-s"
        />
      )}
    </div>
  );
};

export default UserList;
